/**
 * Filtra Schedules PA elegíveis para a página pública:
 * PA + active + exposição + HCS disponível agora.
 */
import dayjs from "dayjs";
import { isProntoAtendimentoSchedule } from "./mapScheduleToConfig";
import {
  fetchHealthcareServiceById,
  getHealthcareServiceAvailableTimes,
  getHealthcareServiceIdFromSchedule,
  isProntoHealthcareServiceWithinWindow,
} from "./isProntoHealthcareServiceAvailableNow";

/** Mesma regra de schedule-exposure usada em PractitionerCards. */
export function hasScheduleExposureEnabled(schedule: fhir5.Schedule): boolean {
  const extensions = schedule.extension;
  if (!extensions?.length) return true;

  const exposureExtension = extensions.find(
    (ext) =>
      ext.url ===
      "https://fluxmed.com.br/fhir/StructureDefinition/schedule-exposure",
  );
  if (!exposureExtension) return true;
  return exposureExtension.valueBoolean === true;
}

/** Schedule PA ativo com exposição pública (sem checar HCS). */
export function isActiveProntoScheduleCandidate(
  schedule: fhir5.Schedule,
): boolean {
  return (
    isProntoAtendimentoSchedule(schedule) &&
    schedule.active !== false &&
    hasScheduleExposureEnabled(schedule)
  );
}

async function resolveHealthcareService(
  schedule: fhir5.Schedule,
  healthcareServicesById?: Map<string, fhir5.HealthcareService>,
): Promise<fhir5.HealthcareService | undefined> {
  const hcsId = getHealthcareServiceIdFromSchedule(schedule);
  if (!hcsId) return undefined;

  const fromBundle = healthcareServicesById?.get(hcsId);
  if (fromBundle) return fromBundle;

  return fetchHealthcareServiceById(hcsId);
}

/**
 * Visão clínica: Schedule ativo + HCS dentro da janela (sem fallback público).
 */
export function isClinicProntoScheduleWithinHours(
  schedule: fhir5.Schedule,
  hcs: fhir5.HealthcareService | undefined,
  toleranceMinutes = 0,
  at: dayjs.Dayjs = dayjs(),
): boolean {
  if (!isProntoAtendimentoSchedule(schedule) || schedule.active === false) {
    return false;
  }
  if (!hcs) return false;

  const times = getHealthcareServiceAvailableTimes(hcs);
  if (!times.length) return false;

  return isProntoHealthcareServiceWithinWindow(hcs, at, toleranceMinutes);
}

/**
 * Verifica disponibilidade HCS; se jornada não veio persistida, confia em Schedule.active.
 */
function isScheduleAvailableNow(
  schedule: fhir5.Schedule,
  hcs: fhir5.HealthcareService | undefined,
  toleranceMinutes = 0,
): boolean {
  if (!hcs) {
    // HCS inacessível — mantém agenda ativa visível (evita sumir por falha de rede)
    return schedule.active !== false;
  }

  const times = getHealthcareServiceAvailableTimes(hcs);
  if (!times.length) {
    // PA ativo sem availableTime no HCS — não bloqueia (config incompleta no servidor)
    return schedule.active !== false;
  }

  return isProntoHealthcareServiceWithinWindow(hcs, dayjs(), toleranceMinutes);
}

/**
 * Retorna apenas Schedules PA disponíveis neste momento (janela HCS de hoje).
 * Aceita HCS já incluídos no Bundle para evitar fetch extra e falhas silenciosas.
 */
export async function filterAvailableProntoSchedules(
  schedules: fhir5.Schedule[],
  healthcareServicesById?: Map<string, fhir5.HealthcareService>,
  toleranceMinutes = 0,
): Promise<fhir5.Schedule[]> {
  const candidates = schedules.filter(isActiveProntoScheduleCandidate);

  const checks = await Promise.all(
    candidates.map(async (schedule) => {
      const hcs = await resolveHealthcareService(
        schedule,
        healthcareServicesById,
      );
      if (!isScheduleAvailableNow(schedule, hcs, toleranceMinutes)) {
        return null;
      }
      return schedule;
    }),
  );

  return checks.filter((s): s is fhir5.Schedule => Boolean(s));
}
